<template>
  <div
    class="rmr-segmented"
    :class="[
      `rmr-segmented--${variant}`,
      `rmr-segmented--${size}`,
      { 'rmr-segmented--block': block },
    ]"
    role="tablist"
  >
    <button
      v-for="item in items"
      :key="item.value"
      type="button"
      role="tab"
      class="rmr-segmented__item"
      :class="{ 'rmr-segmented__item--active': item.value === value }"
      :aria-selected="item.value === value ? 'true' : 'false'"
      :title="item.title || item.label"
      :disabled="item.disabled || false"
      @click="select(item)"
    >
      <v-icon v-if="item.icon" class="rmr-segmented__icon" :size="iconSize">
        {{ item.icon }}
      </v-icon>
      <span v-if="item.label">{{ item.label }}</span>
    </button>
  </div>
</template>

<script>
export default {
  name: "RmrSegmentedControl",
  model: {
    prop: "value",
    event: "input",
  },
  props: {
    value: {
      type: [String, Number, Boolean],
      default: "",
    },
    // Each item: { value, label, icon?, title?, disabled? }
    items: {
      type: Array,
      required: true,
    },
    // plain: light surfaces — on-dark: gradient / dark surfaces (hero)
    variant: {
      type: String,
      default: "plain",
      validator: (value) => ["plain", "on-dark"].includes(value),
    },
    size: {
      type: String,
      default: "sm",
      validator: (value) => ["sm", "md"].includes(value),
    },
    block: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    iconSize() {
      return this.size === "md" ? 16 : 14;
    },
  },
  methods: {
    select(item) {
      if (item.disabled || item.value === this.value) return;
      this.$emit("input", item.value);
    },
  },
};
</script>

<style lang="scss" scoped>
.rmr-segmented {
  display: inline-flex;
  gap: 2px;
  padding: 3px;
  border-radius: $rmr-radius-md;
}

.rmr-segmented--block {
  display: flex;
  width: 100%;

  .rmr-segmented__item {
    flex: 1;
    justify-content: center;
  }
}

.rmr-segmented__item {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  border: none;
  background: transparent;
  border-radius: $rmr-radius-sm;
  font-family: inherit;
  font-weight: 600;
  white-space: nowrap;
  cursor: pointer;
  transition: background 0.15s ease, color 0.15s ease;

  &:disabled {
    opacity: 0.45;
    cursor: not-allowed;
  }

  &:focus-visible {
    outline: 2px solid $rmr-indigo;
    outline-offset: 1px;
  }
}

.rmr-segmented__item--active {
  font-weight: 700;
}

// Icons follow the label color in every variant and state (Vuetify would
// otherwise paint them in its default grey).
.rmr-segmented__icon.v-icon {
  color: inherit;
}

// Sizes
.rmr-segmented--sm .rmr-segmented__item {
  font-size: 12px;
  padding: 6px 14px;
}

.rmr-segmented--md .rmr-segmented__item {
  font-size: 13px;
  padding: 8px 18px;
}

// Variant: plain — light surfaces
.rmr-segmented--plain {
  background: $rmr-surface-2;
  border: 1px solid $rmr-border;

  .rmr-segmented__item {
    color: $rmr-text-muted;

    &:hover:not(:disabled):not(.rmr-segmented__item--active) {
      color: $rmr-text-medium;
    }
  }

  .rmr-segmented__item--active {
    background: $rmr-surface-0;
    color: $rmr-text-primary;
    box-shadow: 0 1px 2px rgba(15, 23, 42, 0.06);
  }
}

// Variant: on-dark — gradient / dark surfaces
.rmr-segmented--on-dark {
  background: rgba(255, 255, 255, 0.12);
  border: 1px solid rgba(255, 255, 255, 0.18);

  .rmr-segmented__item {
    color: rgba(255, 255, 255, 0.72);
    letter-spacing: 0.5px;

    &:hover:not(:disabled):not(.rmr-segmented__item--active) {
      color: $rmr-surface-0;
    }
  }

  .rmr-segmented__item--active {
    background: $rmr-surface-0;
    color: $rmr-indigo-dark;
    box-shadow: 0 1px 3px rgba(15, 23, 42, 0.18);
  }
}
</style>
